/* DevicesViewComponent.js */

"use strict";
const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./DevicesViewRenderer");
const PagingFeed = require("./PagingDeviceFeed");
const DeviceTypeColumn = require("./column_renderers/device_type/DeviceTypeComponent");
const UptimeColumn = require("./column_renderers/uptime/UptimeComponent");

class DevicesViewComponent extends EventEmitter {
    constructor(
        logger,
        gameEditor,
        userSession,
        LicenseRestriction,
        MUF,
        solutionsManager,
        storageFactory,
        DeviceEntity
    ) {
        super();
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._modalUserFeedback = MUF;

        this._userSession = userSession;
        this._licenseRestriction = LicenseRestriction;
        this._storageFactory = storageFactory;
        this._osImageServerFeed = undefined;
        this._engineZoneFeed = undefined;
        // TODO Quick hack to customize DeviceDetailsView based on its parent solution (Device Manager or Config Drift)
        // To be refactored with separate views once form layout / style specifications have matured
        this._solutionDefinition = solutionsManager.getActiveSolution();
        this._solutionName = this._solutionDefinition.name || "";
        this._gridKey = _.camelCase(this._solutionName) + "_" + "devicesView";
        this._device = undefined;
        this._site = undefined;
        this._DeviceEntity = DeviceEntity;
        this._renderer = new Renderer(this);

        this._registerListeners();
    }

    get columnConfig() {
        if (this.columns) {
            return this.columns;
        }
        this.columns = {
            detectedTypeBase: {
                label: "Type",
                columnRendererComponent: DeviceTypeColumn,
                entityColumn: true,
                width: 45,
                resizable: false,
                priority: 1,
                shown: true,
            },
            name: {
                label: "Name",
                shown: true,
                width: 300,
                priority: 2,
            },
            description: {
                label: "Description",
                shown: true,
                width: 300,
                priority: 3,
            },
            ip: {
                label: "IP Address",
                path: "connectionInformation.ip",
                shown: true,
                width: 140,
                priority: 4,
            },
            detectedVendor: {
                label: "Vendor",
                shown: true,
                priority: 5,
                width: 140,
            },
            detectedHostName: {
                label: "Hostname",
                shown: true,
                priority: 6,
                width: 140,
            },
            detectedOs: {
                label: "OS",
                shown: true,
                priority: 7,
                width: 140,
            },
            detectedSoftwareVersion: {
                label: "OS Version",
                shown: true,
                priority: 8,
                width: 140,
            },
            detectedSerialNumber: {
                label: "Serial",
                shown: true,
                width: 140,
                priority: 9,
            },
            detectedSku: {
                label: "SKUs",
                shown: true,
                width: 140,
                priority: 10,
            },
            detectedUptime: {
                label: "Up Time",
                columnRendererComponent: UptimeColumn,
                shown: true,
                width: 120,
                priority: 11,
            },
        };
        return this.columns;
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    _registerSiteListeners() {}

    _unregisterSiteListeners() {}

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    async shutdown() {
        this._unregisterListeners();
        return this._renderer.shutdown().then(
            () => {
                this._device = undefined;
                this._logger = undefined;
                this._gameEditor = undefined;
                this._renderer = undefined;
            },
            (err) => {
                this._logger.warn(err, "SiteDetailsViewComponent.shutdown() error.");
            }
        );
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    isDirty() {
        return this._site.isDirty();
    }

    get gameEditor() {
        return this._gameEditor;
    }

    get logger() {
        return this._logger;
    }

    /**
     * Get the engines that should be available in the drop down list.
     */

    async whenModelIsReady() {
        await this._gameEditor.whenSiteIsReady();
        this._site = this._gameEditor.getSite();

        this._userGridData = this._storageFactory.getRemoteStorageInstanceForEntityGrid(
            "entity_grid_gl",
            this._gridKey + "_selected"
        );
        const userSorting = this._userGridData.getSorting() || {};
        this._patchSorting(userSorting);

        const childSites = await this._gameEditor._siteEntity.loadSites(
            this._gameEditor._activeOrgId,
            {
                ascendantIds: this._site.getId(),
            },
            {id: true, name: true}
        );

        const siteIds = childSites.map((site) => site.getId());
        siteIds.push(this._site.getId());

        this._pagingDevicesFeed = new PagingFeed({
            entityDef: this._DeviceEntity,
            feedKey: this._gridKey + "_feed",
            activeOrgId: this._gameEditor._activeOrgId,
            columnConfig: this.columnConfig,
            sort: userSorting,
            filter: {siteId: {$in: siteIds}},
        });

        return this._pagingDevicesFeed.whenFeedReady();
    }

    _patchSorting(sorting) {
        // add second sorting order by name
        if (sorting && !sorting.name && this.columnConfig.name) {
            sorting.name = 1;
        }
    }

    onUserSorted(sortData) {
        sortData = [].concat(sortData);
        let sort = {};

        sortData.forEach((sortData) => {
            if (!sortData || !sortData.sortColumn || !this.columnConfig[sortData.sortColumn]) {
                return;
            }
            const sortCriteria =
                this.columnConfig[sortData.sortColumn].valuePath ||
                this.columnConfig[sortData.sortColumn].path ||
                sortData.sortColumn;
            sort[sortCriteria] = sortData.sort === "asc" ? 1 : -1;
        });

        this._patchSorting(sort);

        this._pagingDevicesFeed.setSort(sort);
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = DevicesViewComponent;

DevicesViewComponent.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.userSession",
    "$teve.licenseRestriction",
    "$teve.ui.userFeedback",
    "$teve.solutionsManager",
    "$teve.storageFactory",
    "$teve.entities.Device",
];

DevicesViewComponent.$displayDetails = {
    type: "View",
    name: "Devices",
    icon: "configdrift.device_details",
    shown: true,
    title: "Devices",
    order: 3,
};
